import { useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, patch } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Skeleton } from "@/components/common/EmptyState";
import { Avatar } from "@/components/common/Avatar";
import { cn } from "@/lib/utils";
import { GripVertical } from "lucide-react";

const PALETTE = ["#8B5CF6", "#2563EB", "#F59E0B", "#22C55E", "#EC4899", "#0EA5E9", "#EF4444", "#94A3B8"];

function colorFor(moduleId) {
  if (!moduleId) return "#94A3B8";
  let hash = 0;
  for (const c of moduleId) hash = (hash * 31 + c.charCodeAt(0)) % PALETTE.length;
  return PALETTE[hash];
}

function mondayOf(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function buildWeeks() {
  const start = mondayOf(new Date());
  return Array.from({ length: 8 }, (_, i) => {
    const s = new Date(start);
    s.setDate(start.getDate() + i * 7);
    const e = new Date(s);
    e.setDate(s.getDate() + 6);
    return { start: s, end: e, label: s.toLocaleDateString("en-US", { month: "short", day: "numeric" }) };
  });
}

function weekIndexOf(item, weeks) {
  if (!item.allocated_date_range?.start) return -1;
  const d = new Date(item.allocated_date_range.start);
  return weeks.findIndex((w) => d >= w.start && d <= w.end);
}

function ownerModuleId(item, byId) {
  let cur = item;
  let guard = 0;
  while (cur && cur.type !== "Module" && guard < 10) {
    cur = byId[cur.parent_id];
    guard++;
  }
  return cur?.id;
}

export default function AllocationBoard() {
  const { project } = useOutletContext();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { me } = useApp();
  const [dragId, setDragId] = useState(null);
  const weeks = useMemo(() => buildWeeks(), []);

  const { data: allItems = [], isLoading } = useQuery({ queryKey: ["items", project.id], queryFn: () => get(`/pm/projects/${project.id}/items`) });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const { data: company } = useQuery({ queryKey: ["settings-company"], queryFn: () => get("/settings/company") });

  const byId = useMemo(() => Object.fromEntries(allItems.map((i) => [i.id, i])), [allItems]);
  const umap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const members = (project.members || []).map((m) => umap[m.user_id]).filter(Boolean);

  const allocatable = allItems.filter((i) => !i.in_planning && (i.type === "Task" || i.type === "Subtask"));
  const unallocated = allocatable.filter((i) => !i.allocated_date_range);
  const capacity = (company?.daily_hours || 8) * (company?.work_days?.length || 5);

  const reschedule = useMutation({
    mutationFn: ({ wid, body }) => patch(`/pm/items/${wid}`, { ...body, _actor: me?.id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["items", project.id] }),
  });

  const onDrop = (userId, weekIdx) => {
    if (!dragId) return;
    reschedule.mutate({
      wid: dragId,
      body: { assignee_id: userId, allocated_date_range: { start: weeks[weekIdx].start.toISOString(), end: weeks[weekIdx].end.toISOString() } },
    });
    setDragId(null);
  };

  if (isLoading) return <Skeleton className="h-96" />;

  return (
    <div>
      <PageHeader title="Resource Allocation" subtitle="Drag tasks onto a person's week to schedule and assign them." />

      <div className="flex gap-4">
        <div className="w-56 shrink-0 rounded-xl border border-border bg-card p-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Unallocated ({unallocated.length})</div>
          <div className="space-y-1.5">
            {unallocated.map((t) => {
              const modId = ownerModuleId(t, byId);
              return (
                <div key={t.id} draggable onDragStart={() => setDragId(t.id)} onDragEnd={() => setDragId(null)}
                  data-testid={`unalloc-${t.id}`}
                  className="flex cursor-grab items-center gap-1.5 rounded-md border border-border px-2 py-1.5 text-xs">
                  <GripVertical className="h-3 w-3 shrink-0 text-muted-foreground" />
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: colorFor(modId) }} />
                  <span className="flex-1 truncate">{t.title}</span>
                  <span className="text-muted-foreground">{t.estimated_hours || 0}h</span>
                </div>
              );
            })}
            {unallocated.length === 0 && <p className="text-xs text-muted-foreground">Nothing unallocated.</p>}
          </div>
        </div>

        <div className="flex-1 overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 min-w-[160px] border-b border-r border-border bg-card px-3 py-2 text-left font-semibold">Team Member</th>
                {weeks.map((w, i) => <th key={i} className="min-w-[140px] border-b border-border px-2 py-2 text-center font-semibold">{w.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {members.map((u) => (
                <tr key={u.id}>
                  <td className="sticky left-0 z-10 border-r border-border bg-card px-3 py-2">
                    <div className="flex items-center gap-2"><Avatar user={u} size="xs" /><span className="font-medium">{u.name}</span></div>
                  </td>
                  {weeks.map((w, wi) => {
                    const chips = allocatable.filter((t) => t.assignee_id === u.id && weekIndexOf(t, weeks) === wi);
                    const hours = chips.reduce((a, t) => a + (t.estimated_hours || 0), 0);
                    const pct = hours / capacity;
                    return (
                      <td key={wi} data-testid={`alloc-cell-${u.id}-${wi}`}
                        onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(u.id, wi)}
                        className={cn("border-t border-border p-1.5 align-top",
                          pct > 1.2 ? "bg-red-100 dark:bg-red-500/15" : pct > 1.0 ? "bg-amber-100 dark:bg-amber-500/15" : "")}>
                        <div className="space-y-1">
                          {chips.map((t) => {
                            const modId = ownerModuleId(t, byId);
                            return (
                              <div key={t.id} draggable onDragStart={() => setDragId(t.id)} onDragEnd={() => setDragId(null)}
                                onClick={() => navigate(`/pm/projects/${project.id}/items/${t.id}`)}
                                data-testid={`chip-${t.id}`}
                                className="cursor-pointer rounded-md px-1.5 py-1 text-[11px] text-white"
                                style={{ background: colorFor(modId) }} title={`${t.title} · ${t.estimated_hours || 0}h`}>
                                <div className="truncate">{t.title}</div>
                                <div className="opacity-80">{t.estimated_hours || 0}h</div>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
